import {
  AccountAccess,
  AccountMember,
  AccountRole,
  Credentials,
  CurrentUser,
  MembershipStatus,
  ProfileUpdate,
  Registration,
  SecurityQuestionsStatus,
  SetSecurityQuestions,
} from '../types/auth';
import { ApiError, api, seg } from './client';

export const login = (credentials: Credentials): Promise<CurrentUser> =>
  api.post<CurrentUser>('/auth/login', credentials);

/** Registers and signs in the user. Grants no account access by itself. */
export const register = (registration: Registration): Promise<CurrentUser> =>
  api.post<CurrentUser>('/auth/register', registration);

/**
 * Starts a new operational account owned by the signed-in user, named after the
 * program or team it tracks. Callable any time, more than once — each account needs
 * a name distinct from the caller's others.
 */
export const createAccount = (name: string): Promise<AccountAccess> =>
  api.post<AccountAccess>('/accounts', { name });

/**
 * Picks (or re-attempts payment for) the signed-in person's own platform-wide role —
 * independent of any program. Free today, so this always activates immediately; once
 * real pricing is on, it instead returns a checkoutUrl to redirect to.
 */
export const selectRole = (
  role: AccountRole
): Promise<{ status: MembershipStatus; checkoutUrl: string | null }> =>
  api.post<{ status: MembershipStatus; checkoutUrl: string | null }>('/me/role', { role });

export const logout = (): Promise<void> => api.post<void>('/auth/logout', {});

/**
 * Emails a reset link if the address is registered. The response looks identical
 * either way — see the server-side PasswordResetService doc — so this alone can't be
 * used to check whether an email has an account. Not currently used by the sign-in
 * page's own "Forgot password?" link — see the security-questions flow below — but
 * kept working for when a real transactional email provider is wired up.
 */
export const forgotPassword = (email: string): Promise<void> =>
  api.post<void>('/auth/forgot-password', { email });

/** Redeems a password-reset token (from an emailed or admin-issued link) for a new password. */
export const resetPassword = (token: string, newPassword: string): Promise<void> =>
  api.post<void>('/auth/reset-password', { token, newPassword });

// ------------------------------------------------------- security questions

/** The signed-in person's own security questions (never the answers). */
export const fetchSecurityQuestionsStatus = (): Promise<SecurityQuestionsStatus> =>
  api.get<SecurityQuestionsStatus>('/auth/security-questions');

/** Replaces all three of the signed-in person's security questions and answers. */
export const setSecurityQuestions = (payload: SetSecurityQuestions): Promise<void> =>
  api.put<void>('/auth/security-questions', payload);

/**
 * Step one of the self-service, no-email recovery flow: the question text for an
 * address, or `available: false` either way if there's nothing to answer — see the
 * server-side SecurityQuestionsLookupDto doc.
 */
export const lookupSecurityQuestions = (
  email: string
): Promise<{ available: boolean; questions: string[] }> =>
  api.post<{ available: boolean; questions: string[] }>('/auth/security-questions/lookup', { email });

/** Step two: resets the password immediately if all three answers match. */
export const resetPasswordWithAnswers = (
  email: string,
  answer1: string,
  answer2: string,
  answer3: string,
  newPassword: string
): Promise<void> =>
  api.post<void>('/auth/security-questions/reset', { email, answer1, answer2, answer3, newPassword });

/** Edits the signed-in person's own display name, email, and optionally password. */
export const updateProfile = (update: ProfileUpdate): Promise<CurrentUser> =>
  api.put<CurrentUser>('/auth/profile', update);

/**
 * The signed-in user, or null when there is no valid session. A 401 here is the
 * expected answer for a visitor rather than a failure, so it is not propagated.
 */
export const fetchCurrentUser = async (): Promise<CurrentUser | null> => {
  try {
    return await api.get<CurrentUser>('/auth/me');
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthenticated) {
      return null;
    }
    throw error;
  }
};

// ----------------------------------------------------------------- members

export const fetchMembers = (accountId: string): Promise<AccountMember[]> =>
  api.get<AccountMember[]>(`/accounts/${seg(accountId)}/members`);

/** Grants access to an already-registered person. */
export const addMember = (
  accountId: string,
  email: string,
  role: AccountRole
): Promise<AccountMember> =>
  api.post<AccountMember>(`/accounts/${seg(accountId)}/members`, { email, role });

export const updateMemberRole = (
  accountId: string,
  email: string,
  role: AccountRole
): Promise<AccountMember> =>
  api.put<AccountMember>(`/accounts/${seg(accountId)}/members`, { email, role });

export const removeMember = (accountId: string, userId: string): Promise<void> =>
  api.delete(`/accounts/${seg(accountId)}/members/${seg(userId)}`);

/**
 * Issues a one-time password-reset link for a member, for the caller (an account
 * admin) to send them directly — sidesteps needing a working outbound-email setup.
 */
export const issueMemberResetLink = (accountId: string, userId: string): Promise<{ link: string }> =>
  api.post<{ link: string }>(`/accounts/${seg(accountId)}/members/${seg(userId)}/reset-password-link`, {});
